import { ELocalStorageKey } from '/@/types'
import type { OSDVisible } from '/@/types/device'

const TSA_PAGE_STATE_VERSION = 1
const TSA_PAGE_STATE_KEY_PREFIX = 'DJI_CREATE_VITE_H5_APP:tsa_page_state'

export type TsaLiveWindowType = 'dock' | 'drone'

export interface TsaPanelsState {
  dockExpanded: boolean,
  aircraftExpanded: boolean,
}

export interface TsaLiveWindowState {
  type: TsaLiveWindowType,
  targetSn: string,
  gatewaySn?: string,
  title: string,
  liveState: boolean,
  updatedAt: number,
}

export interface TsaCockpitState {
  dockSn: string,
  droneSn: string,
  dockName: string,
  droneName: string,
  osd: OSDVisible,
  flightControlActive: boolean,
  updatedAt: number,
}

export interface TsaPageState {
  version: number,
  panels: TsaPanelsState,
  osd?: OSDVisible,
  liveWindow?: TsaLiveWindowState,
  liveWindows?: TsaLiveWindowState[],
  cockpit?: TsaCockpitState,
}

function getDefaultState (): TsaPageState {
  return {
    version: TSA_PAGE_STATE_VERSION,
    panels: {
      dockExpanded: false,
      aircraftExpanded: false,
    },
  }
}

function getStorageKey () {
  const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)
  const userId = localStorage.getItem(ELocalStorageKey.UserId)
  if (!workspaceId || !userId) {
    return null
  }
  return `${TSA_PAGE_STATE_KEY_PREFIX}:${workspaceId}:${userId}`
}

function isObject (value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function normalizeOsd (osd: unknown): OSDVisible | undefined {
  if (!isObject(osd)) {
    return undefined
  }
  return {
    sn: String(osd.sn || ''),
    model: String(osd.model || ''),
    model_key: String(osd.model_key || ''),
    callsign: String(osd.callsign || ''),
    visible: osd.visible === true,
    is_dock: osd.is_dock === true,
    gateway_sn: String(osd.gateway_sn || ''),
    gateway_callsign: String(osd.gateway_callsign || ''),
    gateway_model: String(osd.gateway_model || ''),
    gateway_model_key: String(osd.gateway_model_key || ''),
    payloads: Array.isArray(osd.payloads) ? osd.payloads.map(payload => ({ ...payload })) : null,
  }
}

function normalizeLiveWindow (liveWindow: unknown): TsaLiveWindowState | undefined {
  if (!isObject(liveWindow)) {
    return undefined
  }
  if (liveWindow.type !== 'dock' && liveWindow.type !== 'drone') {
    return undefined
  }
  return {
    type: liveWindow.type,
    targetSn: String(liveWindow.targetSn || ''),
    gatewaySn: liveWindow.gatewaySn ? String(liveWindow.gatewaySn) : undefined,
    title: String(liveWindow.title || ''),
    liveState: liveWindow.liveState === true,
    updatedAt: Number(liveWindow.updatedAt) || 0,
  }
}

function normalizeLiveWindows (liveWindows: unknown): TsaLiveWindowState[] | undefined {
  if (!Array.isArray(liveWindows)) {
    return undefined
  }

  const dedupedWindows = new Map<string, TsaLiveWindowState>()
  liveWindows.forEach(liveWindow => {
    const normalizedLiveWindow = normalizeLiveWindow(liveWindow)
    if (normalizedLiveWindow?.targetSn) {
      dedupedWindows.set(`${normalizedLiveWindow.type}:${normalizedLiveWindow.targetSn}`, normalizedLiveWindow)
    }
  })

  return Array.from(dedupedWindows.values())
}

function normalizeCockpit (cockpit: unknown): TsaCockpitState | undefined {
  if (!isObject(cockpit)) {
    return undefined
  }
  const osd = normalizeOsd(cockpit.osd)
  if (!osd) {
    return undefined
  }
  return {
    dockSn: String(cockpit.dockSn || osd.gateway_sn || ''),
    droneSn: String(cockpit.droneSn || osd.sn || ''),
    dockName: String(cockpit.dockName || osd.gateway_callsign || ''),
    droneName: String(cockpit.droneName || osd.callsign || ''),
    osd,
    flightControlActive: cockpit.flightControlActive === true,
    updatedAt: Number(cockpit.updatedAt) || 0,
  }
}

function normalizeState (state: unknown): TsaPageState | null {
  if (!isObject(state) || state.version !== TSA_PAGE_STATE_VERSION) {
    return null
  }

  const nextState = getDefaultState()
  if (isObject(state.panels)) {
    nextState.panels = {
      dockExpanded: state.panels.dockExpanded === true,
      aircraftExpanded: state.panels.aircraftExpanded === true,
    }
  }

  const osd = normalizeOsd(state.osd)
  if (osd) {
    nextState.osd = osd
  }

  const liveWindow = normalizeLiveWindow(state.liveWindow)
  if (liveWindow) {
    nextState.liveWindow = liveWindow
  }

  const liveWindows = normalizeLiveWindows(state.liveWindows)
  if (liveWindows) {
    nextState.liveWindows = liveWindows
  }

  const cockpit = normalizeCockpit(state.cockpit)
  if (cockpit) {
    nextState.cockpit = cockpit
  }

  return nextState
}

function writeTsaPageState (state: TsaPageState) {
  const key = getStorageKey()
  if (!key) {
    return
  }
  try {
    localStorage.setItem(key, JSON.stringify(state))
  } catch (error) {
    console.warn('save tsa page state failed:', error)
  }
}

function updateTsaPageState (updater: (state: TsaPageState) => TsaPageState) {
  const key = getStorageKey()
  if (!key) {
    return
  }
  const state = getTsaPageState() || getDefaultState()
  writeTsaPageState(updater(state))
}

function cloneOsd (osd: OSDVisible): OSDVisible {
  return {
    ...osd,
    payloads: osd.payloads ? osd.payloads.map(payload => ({ ...payload })) : null,
  }
}

export function getTsaPageState (): TsaPageState | null {
  const key = getStorageKey()
  if (!key) {
    return null
  }
  const value = localStorage.getItem(key)
  if (!value) {
    return null
  }
  try {
    return normalizeState(JSON.parse(value))
  } catch (error) {
    console.warn('parse tsa page state failed:', error)
    return null
  }
}

export function saveTsaPanelsState (panels: Partial<TsaPanelsState>) {
  updateTsaPageState(state => ({
    ...state,
    panels: {
      ...state.panels,
      ...panels,
    },
  }))
}

export function saveTsaOsdState (osd: OSDVisible) {
  updateTsaPageState(state => ({
    ...state,
    osd: cloneOsd(osd),
  }))
}

export function clearTsaOsdState () {
  updateTsaPageState(state => {
    const nextState = { ...state }
    delete nextState.osd
    return nextState
  })
}

export function saveTsaLiveWindowState (liveWindow: Omit<TsaLiveWindowState, 'updatedAt'> & { updatedAt?: number }) {
  updateTsaPageState(state => ({
    ...state,
    liveWindow: {
      ...liveWindow,
      updatedAt: liveWindow.updatedAt || Date.now(),
    },
  }))
}

export function saveTsaLiveWindowsState (liveWindows: Array<Omit<TsaLiveWindowState, 'updatedAt'> & { updatedAt?: number }>) {
  updateTsaPageState(state => {
    const nextState = {
      ...state,
      liveWindows: liveWindows.map(liveWindow => ({
        ...liveWindow,
        updatedAt: liveWindow.updatedAt || Date.now(),
      })),
    }
    delete nextState.liveWindow
    return nextState
  })
}

export function clearTsaLiveWindowState () {
  updateTsaPageState(state => {
    const nextState = { ...state }
    delete nextState.liveWindow
    delete nextState.liveWindows
    return nextState
  })
}

export function saveTsaCockpitState (cockpit: Omit<TsaCockpitState, 'updatedAt'> & { updatedAt?: number }) {
  updateTsaPageState(state => ({
    ...state,
    cockpit: {
      ...cockpit,
      osd: cloneOsd(cockpit.osd),
      updatedAt: cockpit.updatedAt || Date.now(),
    },
  }))
}

export function getTsaCockpitState (): TsaCockpitState | undefined {
  return getTsaPageState()?.cockpit
}
